import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import './ManageCampaign.css';
import CustomPaginationTable from '../../components/table/CustomPaginationTable';
import EditIcon from '../../static/image/editIcon.svg';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import DownloadIcon from '../../static/image/downloadIcon.png';
import ManageCampaignDetail from './ManageCampaignDetail';
import { useDispatch, useSelector } from 'react-redux';
import CommonSuccessModal from "../../components/commonSuccessModal/CommonSuccessModal";
import CommonDeleteModal from "../../components/commonDeleteModal/CommonDeleteModal";
import { decodedJWTPayload } from '../../utils/commonUtil';
import { formatDatetime } from '../../utils/dateUtils';

import {
  fetchPromoCodes,
  fetchCampaignById,
  resetCurrentCampaign,
  updateCampaign,
  createCampaign,
  deleteCampaign,
  resetDeleteStatus,
  exportPromoCodes,
  resetExportStatus,
  downloadExportedFile,
} from '../../redux/manageCampaignSlice';

import {
  PERMISSION_VALUES,
  MENU_IDS,
  PERMISSION_CONSTANTS,
  ROUTES,
  EDIT_MODEL_CREATE,
  EDIT_MODEL_EDIT
} from '../../utils/constant';

function ManageCampaign() {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const location = useLocation();

  const dispatch = useDispatch();
  const {
    promoCodes = { resdata: { content: [] } },
    currentCampaign,
    loading,
    error,
    deleteStatus,
    exportStatus,
    pagination = {
      pageNumber: 0,
      totalRecords: 0,
      pageSize: 10
    }
  } = useSelector((state) => state.manageCampaign);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [permission, setPermission] = useState('');

  const initializedRef = useRef(false);
  const isMountedRef = useRef(true);
  const [currentPageSize, setCurrentPageSize] = useState(pagination.pageSize || 10);

  const getEditModeFromRoute = () => {
    if (location.pathname.includes('/create')) return EDIT_MODEL_CREATE;
    if (location.pathname.includes('/edit/')) return EDIT_MODEL_EDIT;
    return undefined;
  };

  const [editMode, setEditMode] = useState(getEditModeFromRoute());

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      const payload = decodedJWTPayload(token);
      const mnId = payload?.mn_id;

      if (mnId) {
        const jwtPattern = PERMISSION_CONSTANTS.CHECKS.getJwtPattern(MENU_IDS.MANAGE_CAMPAIGN);
        const match = mnId.match(jwtPattern);
        if (match) {
          setPermission(match[1]);
        }
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    if (!initializedRef.current && isMountedRef.current) {
      initializedRef.current = true;
      const pageSize = pagination.pageSize || 10;
      setCurrentPageSize(pageSize);
      dispatch(fetchPromoCodes({ page: 0, size: pageSize }));
    }

    return () => {
      isMountedRef.current = false;
      dispatch(resetCurrentCampaign());
    };
  }, [dispatch]);

  useEffect(() => {
    const newEditMode = getEditModeFromRoute();
    setEditMode(newEditMode);

    if (newEditMode === EDIT_MODEL_EDIT && campaignId) {
      handleFetchCampaignDetails(campaignId);
    }
  }, [location.pathname, campaignId]);

  useEffect(() => {
    if (pagination.pageSize && pagination.pageSize !== currentPageSize) {
      setCurrentPageSize(pagination.pageSize);
    }
  }, [pagination.pageSize]);

  useEffect(() => {
    if (deleteStatus === 'success') {
      setErrorMessage('Campaign deleted successfully');
      setIsError(false);
      setShowErrorModal(true);
      dispatch(resetDeleteStatus());
    } else if (deleteStatus === 'failed') {
      setErrorMessage(error);
      setIsError(true);
      setShowErrorModal(true);
      dispatch(resetDeleteStatus());
    }
  }, [deleteStatus, error, dispatch]);

  useEffect(() => {
    if (exportStatus === 'failed') {
      setErrorMessage(error);
      setIsError(true);
      setShowErrorModal(true);
      dispatch(resetExportStatus());
    }
  }, [exportStatus, error, dispatch]);

  const handleFetchCampaignDetails = async (campaignId) => {
    try {
      await dispatch(fetchCampaignById(campaignId)).unwrap();
    } catch (error) {
      setErrorMessage(error.message);
      setIsError(true);
      setShowErrorModal(true);
      navigate(ROUTES.MANAGE_CAMPAIGN);
    }
  };

  const isDownloadEnabled = [
    PERMISSION_VALUES.FULL_ACCESS,
    PERMISSION_VALUES.WRITE,
    PERMISSION_VALUES.READ
  ].includes(permission);

  const isEditEnabled = [
    PERMISSION_VALUES.FULL_ACCESS,
    PERMISSION_VALUES.WRITE
  ].includes(permission);

  const isDeleteEnabled = permission === PERMISSION_VALUES.FULL_ACCESS;
  const isAddEnabled = [
    PERMISSION_VALUES.FULL_ACCESS,
    PERMISSION_VALUES.WRITE
  ].includes(permission);

  const handlePageChange = (page) => {
    const apiPage = page - 1;
    dispatch(fetchPromoCodes({ page: apiPage, size: currentPageSize }));
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setCurrentPageSize(newPerPage);
    dispatch(fetchPromoCodes({ page: 0, size: newPerPage }));
  };

  const handleDownload = (campaign) => {
    if (!isDownloadEnabled) return;

    try {
      setErrorMessage('');
      const exportParams = {
        "uuid": campaign.uuid
      };
      dispatch(exportPromoCodes(exportParams))
        .then(() => {
          setTimeout(() => {
            dispatch(downloadExportedFile());
          }, 100);
        });
    } catch (error) {
      console.error('Failed to export campaign:', error);
      setErrorMessage(error.message || 'Failed to export campaign data');
      setIsError(true);
      setShowErrorModal(true);
    }
  };

  const handleDelete = (campaign) => {
    if (!isDeleteEnabled) return;

    setCampaignToDelete(campaign);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deleteCampaign(campaignToDelete.uuid)).unwrap();
      setShowDeleteConfirm(false);
      setCampaignToDelete(null);
      if (editMode) {
        navigate(ROUTES.MANAGE_CAMPAIGN);
      }
    } catch (err) {
      console.error('Delete campaign failed:', err);
      setErrorMessage(err.message || 'Failed to delete campaign');
      setIsError(true);
      setShowErrorModal(true);
      setShowDeleteConfirm(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setCampaignToDelete(null);
  };

  const handleAddNew = () => {
    if (!isAddEnabled) return;

    dispatch(resetCurrentCampaign());
    navigate(ROUTES.CAMPAIGN_CREATE);
  };

  const handleEdit = (campaign) => {
    if (!isEditEnabled) return;

    navigate(`/dashboard/manage-campaign/edit/${campaign.uuid}`);
  };

  const handleCancelDetail = () => {
    navigate(ROUTES.MANAGE_CAMPAIGN);
  };

  const handleSave = async (updatedCampaign) => {
    if ((permission === PERMISSION_VALUES.WRITE && editMode === EDIT_MODEL_CREATE) || permission === PERMISSION_VALUES.READ) return;

    if (!updatedCampaign.campaignName || !updatedCampaign.promoType ||
      !updatedCampaign.startDate || !updatedCampaign.endDate || updatedCampaign.selectedProducts.length === 0) {
      setErrorMessage('Please fill in all required fields (marked with *)');
      setIsError(true);
      setShowErrorModal(true);
      return;
    }

    if (updatedCampaign.promoType === 'General promo') {
      if (!updatedCampaign.quantity || !updatedCampaign.promoCode) {
        setErrorMessage('For General promo, Quantity and Promo code are required fields');
        setIsError(true);
        setShowErrorModal(true);
        return;
      }
    }

    if (updatedCampaign.promoType === 'Unique promo') {
      if (!updatedCampaign.quantity) {
        setErrorMessage('For Unique promo, Quantity is a required field');
        setIsError(true);
        setShowErrorModal(true);
        return;
      }
    }

    if (updatedCampaign.startDate && updatedCampaign.endDate) {
      if (updatedCampaign.endDate <= updatedCampaign.startDate) {
        setErrorMessage('The end date must be later than the start date');
        setIsError(true);
        setShowErrorModal(true);
        return;
      }
    }

    const formatLocalDateTime = (date) => {
      if (!date) return '';

      const d = new Date(date);

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const campaignPayload = {
      campaignName: updatedCampaign.campaignName,
      campaignDesc: updatedCampaign.campaignDescription || '',
      promoType: updatedCampaign.promoType === 'General promo' ? 1 : 2,
      quantity: updatedCampaign.quantity ? Number(updatedCampaign.quantity) : 0,
      priceOff: updatedCampaign.priceOff ? Number(updatedCampaign.priceOff).toFixed(2) : '0.00',
      promoCode: updatedCampaign.promoCode || '',
      rateOff: updatedCampaign.rateOff ? (Number(updatedCampaign.rateOff) / 100).toFixed(2) : '0.00',
      remarks: updatedCampaign.remarks || '',
      startDate: formatLocalDateTime(updatedCampaign.startDate),
      endDate: formatLocalDateTime(updatedCampaign.endDate),
      productUuids: updatedCampaign.selectedProducts
    };

    try {
      setErrorMessage('');

      if (currentCampaign?.resdata?.uuid) {
        await dispatch(updateCampaign({
          uuid: currentCampaign.resdata.uuid,
          campaignData: campaignPayload
        })).unwrap();
      } else {
        await dispatch(createCampaign(campaignPayload)).unwrap();
      }

      dispatch(fetchPromoCodes({ page: pagination.pageNumber, size: currentPageSize }));
      navigate(ROUTES.MANAGE_CAMPAIGN);

    } catch (err) {
      console.error('Save campaign failed:', err);
      setErrorMessage(err.message || 'Failed to save campaign');
      setIsError(true);
      setShowErrorModal(true);
    }
  };

  const transformedCampaigns = (promoCodes.resdata?.content || []).map((campaign, index) => ({
    id: campaign.id,
    name: campaign.campaignName,
    createdBy: campaign.createdBy,
    createdDateTime: formatDatetime(campaign.createdOn),
    updatedBy: campaign.updatedBy || campaign.createdBy,
    updatedDateTime: campaign.updatedOn ? formatDatetime(campaign.updatedOn) : '-',
    rawData: campaign,
    index: index + 1 + (pagination.pageNumber * currentPageSize)
  }));

  const columns = [
    {
      name: 'No.',
      selector: (row) => row.index,
      width: '60px',
    },
    {
      name: 'Campaign Name',
      selector: row => row.name,
    },
    {
      name: 'Created By',
      selector: row => row.createdBy,
    },
    {
      name: 'Created Date/Time',
      selector: row => row.createdDateTime,
    },
    {
      name: 'Updated By',
      selector: row => row.updatedBy,
    },
    {
      name: 'Updated Date/Time',
      selector: row => row.updatedDateTime,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="manage-campaign-actions">
          <img
            src={DownloadIcon}
            alt="download"
            style={{
              width: '20px',
              height: '20px',
              cursor: isDownloadEnabled ? 'pointer' : 'default',
              marginRight: '8px',
              opacity: isDownloadEnabled ? 1 : 0.5
            }}
            onClick={() => handleDownload(row.rawData)}
          />
          <img
            src={EditIcon}
            alt="Edit"
            onClick={() => handleEdit(row.rawData)}
            style={{
              cursor: isEditEnabled ? 'pointer' : 'default',
              marginRight: '8px',
              opacity: isEditEnabled ? 1 : 0.5,
              pointerEvents: isEditEnabled ? 'auto' : 'none'
            }}
          />
          <img
            src={DeleteIcon}
            alt="Delete"
            onClick={() => handleDelete(row.rawData)}
            style={{
              cursor: isDeleteEnabled ? 'pointer' : 'default',
              opacity: isDeleteEnabled ? 1 : 0.5,
              pointerEvents: isDeleteEnabled ? 'auto' : 'none'
            }}
          />
        </div>
      ),
      width: '150px',
    },
  ];

  return (
    <div className="create-user-container">
      <CommonSuccessModal
        showModal={showErrorModal}
        handleClose={() => setShowErrorModal(false)}
        message={errorMessage}
        status={!isError}
      />

      <CommonDeleteModal
        show={showDeleteConfirm}
        onHide={cancelDelete}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        instruction={`Are you sure you want to delete the campaign "${campaignToDelete?.campaignName}"?`}
      />

      {editMode ? (
        <ManageCampaignDetail
          campaign={currentCampaign?.resdata || null}
          onSave={handleSave}
          onCancel={handleCancelDetail}
          permission={permission}
          isCreateMode={editMode === EDIT_MODEL_CREATE}
          editMode={editMode}
        />
      ) : (
        <div className="manage-campaign-container">
          <div className="manage-campaign-content">
            <div className="manage-campaign-datatable-container">
              <CustomPaginationTable
                data={transformedCampaigns}
                columns={columns}
                isHiddenPag={false}
                totalRows={pagination.totalRecords || 0}
                loading={loading}
                perPage={currentPageSize}
                currentPage={(pagination.pageNumber || 0) + 1}
                onPageChange={handlePageChange}
                onPerRowsChange={handlePerRowsChange}
              />
            </div>

            <div className="manage-campaign-footer">
              <button
                className="manage-campaign-add-btn"
                onClick={handleAddNew}
                style={{
                  opacity: isAddEnabled ? 1 : 0.5,
                  cursor: isAddEnabled ? 'pointer' : 'default',
                  pointerEvents: isAddEnabled ? 'auto' : 'none'
                }}
              >
                Add new
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageCampaign;